import { type ReactNode } from 'react';
import {
  Box,
  Chip,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { ArrowRight } from 'lucide-react';

import { Stack } from '$layouts/v2/components/Stack.jsx';
import { TemplateInput } from '$layouts/v2/components/TemplateInput.jsx';
import { KIND_META, KindIcon } from '$layouts/v2/consts';
import type { FieldInfo } from '$layouts/v2/helpers/context.js';

/** Consistent visual grouping used by every step kind. */
export function StepSection({
  title,
  description,
  optional = false,
  children,
}: {
  title: string;
  description: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {optional && <Chip size="small" label="Optional" />}
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {description}
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {children}
      </Stack>
    </Box>
  );
}

/** A template editor with a direct, discoverable way to insert available data. */
export function TemplateField({
  label,
  helpText,
  value,
  fields,
  onChange,
  extra,
  multiline,
  rows,
  readOnly = false,
}: {
  label: string;
  helpText?: string;
  value: string;
  fields: FieldInfo[];
  onChange: (value: string) => void;
  extra?: FieldInfo[];
  multiline?: boolean;
  rows?: number;
  readOnly?: boolean;
}) {
  return (
    <Box>
      <Box sx={{ mb: 0.5 }}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
          {helpText && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
              }}
            >
              {helpText}
            </Typography>
          )}
        </Box>
      </Box>
      <TemplateInput
        value={value}
        fields={fields}
        extra={extra}
        supportsMarkdown
        onChange={onChange}
        multiline={multiline}
        rows={rows}
        editorTitle={label}
        readOnly={readOnly}
      />
    </Box>
  );
}

export function Transitions({
  children,
  description,
}: {
  children: ReactNode;
  description?: string;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'secondary.main',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'secondary.lighter' }}>
        <Typography
          variant="subtitle2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            fontWeight: 700,
          }}
        >
          <ArrowRight size={15} /> Then
        </Typography>
        {description && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {children}
      </Stack>
    </Box>
  );
}

/** Selects the next step for a transition. */
export function TransitionTargetSelect({
  stepOptions,
  value,
  label,
  onChange,
  readOnly = false,
}: {
  stepOptions: { id: string; label: string; kind?: v4.StepKind }[];
  value: string | undefined;
  label?: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) {
  const selectedTarget = stepOptions.find((target) => target.id === value);

  if (readOnly) {
    return (
      <ReadOnlyTextField
        label={label}
        value={selectedTarget ? targetDisplayLabel(selectedTarget) : value}
        emptyLabel="Not set"
      />
    );
  }

  return (
    <TextField
      select
      label={label}
      aria-label="Transition target step"
      value={value ?? ''}
      slotProps={{
        input: {
          startAdornment: selectedTarget ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 1,
                color: selectedTarget.kind
                  ? KIND_META[selectedTarget.kind].color
                  : 'text.secondary',
              }}
            >
              {selectedTarget.kind && <KindIcon kind={selectedTarget.kind} />}
            </Box>
          ) : undefined,
        },

        select: {
          renderValue: (selected) => {
            const target = stepOptions.find((option) => option.id === selected);
            return target ? targetDisplayLabel(target) : '— choose a step —';
          },
        },
      }}
      onChange={(event) => onChange(event.target.value)}
    >
      <MenuItem value="">— choose a step —</MenuItem>
      {stepOptions.map((target) => (
        <MenuItem key={target.id} value={target.id}>
          <Box
            sx={{
              color: target.kind
                ? KIND_META[target.kind].color
                : 'text.secondary',
              mr: 1,
            }}
          >
            {target.kind && <KindIcon kind={target.kind} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {target.label}
            </Typography>
            {target.label !== target.id && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                }}
              >
                {target.id}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
}

function targetDisplayLabel(target: { id: string; label: string }) {
  return target.label || target.id;
}

/** Presents a workflow value with form-field affordance but no edit controls. */
export function ReadOnlyTextField({
  label,
  value,
  emptyLabel = 'Not set',
}: {
  label?: string;
  value: string | undefined;
  emptyLabel?: string;
}) {
  return (
    <TextField
      fullWidth
      label={label}
      value={value || `— ${emptyLabel} —`}
      slotProps={{ htmlInput: { readOnly: true } }}
    />
  );
}
